<template lang="pug">
teleport(to="body")
  .donation-dialog-backdrop(v-if="modelValue" @click="handleBackdropClick")
    .donation-dialog(@click.stop)
      .dialog-header
        h2.dialog-title {{ dialogTitle }}
        button.close-btn(type="button" @click="handleClose" aria-label="Close")
          img(src="@/assets/icons/close.svg" alt="")

      p.dialog-subtitle(v-if="items.length > 1")
        | Set the amount for each item before adding to your cart.

      .item-forms
        .item-form(v-for="row in formRows" :key="row.sticker.id")
          .item-header
            img.item-thumb(v-if="row.sticker.url" :src="row.sticker.url" :alt="row.sticker.title || 'Sticker'")
            .item-meta
              .item-title {{ row.sticker.title || 'Sticker' }}
              .item-mode-label {{ row.modeLabel }}

          template(v-if="row.mode === 'ticketQuantity'")
            .ticket-price-note
              | {{ formatCurrency(row.unitCost) }} per ticket
            .ticket-qty-row
              span.ticket-qty-label Number of tickets
              .shop-qty-stepper
                button.shop-qty-btn(
                  type="button"
                  @click="decrementQuantity(row.sticker.id)"
                  :disabled="row.quantity <= 1"
                  aria-label="Decrease ticket quantity"
                )
                  img(src="@/assets/icons/minus.svg" alt="")
                span.shop-qty-value {{ row.quantity }}
                button.shop-qty-btn.plus(
                  type="button"
                  @click="incrementQuantity(row.sticker.id)"
                  :disabled="row.quantity >= maxQuantity"
                  aria-label="Increase ticket quantity"
                )
                  img(src="@/assets/icons/plus.svg" alt="")
            .line-total Total: {{ formatCurrency(row.unitCost * row.quantity) }}

          template(v-else-if="row.mode === 'sweepstakesTier'")
            .amount-buttons
              button.amount-btn(
                v-for="amount in row.suggestedAmounts"
                :key="`${row.sticker.id}-${amount}`"
                type="button"
                :class="{ active: row.amount === amount }"
                @click="setAmount(row.sticker.id, amount)"
              ) {{ formatCurrency(amount) }}
            .donation-label(v-if="!row.suggestedAmounts.length") Entry amount
            .input-wrapper(v-if="!row.suggestedAmounts.length")
              span.input-icon $
              input.amount-input(
                type="number"
                step="0.01"
                :min="row.minAmount"
                v-model="row.amountInput"
                @input="syncAmountFromInput(row.sticker.id)"
              )

          template(v-else)
            .donation-label Donation amount
            .input-wrapper
              span.input-icon $
              input.amount-input(
                type="number"
                step="0.01"
                :min="row.minAmount"
                v-model="row.amountInput"
                @input="syncAmountFromInput(row.sticker.id)"
              )
            .amount-buttons(v-if="row.suggestedAmounts.length")
              button.amount-btn(
                v-for="amount in row.suggestedAmounts"
                :key="`${row.sticker.id}-${amount}`"
                type="button"
                :class="{ active: row.amount === amount }"
                @click="setAmount(row.sticker.id, amount)"
              ) {{ formatCurrency(amount) }}

          .error-message(v-if="row.error") {{ row.error }}

      .action-buttons
        button.action-btn.primary-btn(
          type="button"
          :disabled="!canSubmit"
          @click="handleSubmit"
        ) Add to cart
        button.action-btn.secondary-btn(type="button" @click="handleClose") Cancel
</template>

<script lang="ts">
import {
  defineComponent, ref, computed, watch, PropType,
} from 'vue';
import {
  CartAmountConfirmEntry,
  CartAmountDialogItem,
} from '@/utils/cartItemAmount';

interface FormRow {
  sticker: any;
  mode: CartAmountDialogItem['mode'];
  modeLabel: string;
  minAmount: number;
  suggestedAmounts: number[];
  amount: number;
  amountInput: string;
  unitCost: number;
  quantity: number;
  error: string;
}

export default defineComponent({
  name: 'DialogCartDonationAmount',
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    items: {
      type: Array as PropType<CartAmountDialogItem[]>,
      default: () => [],
    },
    title: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue', 'confirm'],
  setup(props, { emit }) {
    const formRows = ref<FormRow[]>([]);
    const maxQuantity = 99;

    const dialogTitle = computed(() => (
      props.title || (props.items.length > 1 ? 'Set amounts' : 'Set amount before adding to cart')
    ));

    function formatCurrency(value: number): string {
      return `$${Number(value || 0).toFixed(2)}`;
    }

    function getModeLabel(mode: CartAmountDialogItem['mode']): string {
      if (mode === 'ticketQuantity') return 'Tickets';
      if (mode === 'sweepstakesTier') return 'Sweepstakes entry';
      return 'Donation';
    }

    function buildFormRows(items: CartAmountDialogItem[]): FormRow[] {
      return items.map((item) => {
        const unitCost = item.mode === 'ticketQuantity'
          ? item.minAmount
          : item.initialAmount;
        return {
          sticker: item.sticker,
          mode: item.mode,
          modeLabel: getModeLabel(item.mode),
          minAmount: item.minAmount,
          suggestedAmounts: item.suggestedAmounts,
          amount: item.initialAmount,
          amountInput: String(item.initialAmount),
          unitCost,
          quantity: item.initialQuantity,
          error: '',
        };
      });
    }

    watch(
      () => [props.modelValue, props.items] as const,
      ([visible, items]) => {
        if (visible && items?.length) {
          formRows.value = buildFormRows(items);
        }
      },
      { immediate: true, deep: true },
    );

    function setAmount(stickerId: string, amount: number) {
      formRows.value = formRows.value.map((row) => {
        if (row.sticker.id !== stickerId) return row;
        return {
          ...row,
          amount,
          amountInput: String(amount),
          error: '',
        };
      });
    }

    function syncAmountFromInput(stickerId: string) {
      formRows.value = formRows.value.map((row) => {
        if (row.sticker.id !== stickerId) return row;
        const parsed = parseFloat(row.amountInput);
        if (Number.isNaN(parsed)) {
          return { ...row, error: 'Enter a valid amount', amount: row.minAmount };
        }
        return {
          ...row,
          amount: parsed,
          error: parsed < row.minAmount
            ? `Minimum amount is ${formatCurrency(row.minAmount)}`
            : '',
        };
      });
    }

    function incrementQuantity(stickerId: string) {
      formRows.value = formRows.value.map((row) => {
        if (row.sticker.id !== stickerId || row.quantity >= maxQuantity) return row;
        return { ...row, quantity: row.quantity + 1, error: '' };
      });
    }

    function decrementQuantity(stickerId: string) {
      formRows.value = formRows.value.map((row) => {
        if (row.sticker.id !== stickerId || row.quantity <= 1) return row;
        return { ...row, quantity: row.quantity - 1, error: '' };
      });
    }

    const canSubmit = computed(() => (
      formRows.value.length > 0
      && formRows.value.every((row) => {
        if (row.error) return false;
        if (row.mode === 'ticketQuantity') {
          return row.quantity >= 1 && row.unitCost >= 0;
        }
        return row.amount >= row.minAmount;
      })
    ));

    function handleClose() {
      emit('update:modelValue', false);
    }

    function handleBackdropClick() {
      handleClose();
    }

    function handleSubmit() {
      formRows.value.forEach((row) => syncAmountFromInput(row.sticker.id));
      if (!canSubmit.value) return;

      const payload: CartAmountConfirmEntry[] = formRows.value.map((row) => {
        if (row.mode === 'ticketQuantity') {
          return {
            sticker: row.sticker,
            unitCost: row.unitCost,
            quantity: row.quantity,
          };
        }
        if (row.mode === 'sweepstakesTier') {
          return {
            sticker: row.sticker,
            unitCost: row.amount,
            quantity: 1,
            sweepstakesEntryCost: row.amount,
          };
        }
        return {
          sticker: row.sticker,
          unitCost: row.amount,
          quantity: 1,
          donationAmount: row.amount,
        };
      });

      emit('confirm', payload);
      emit('update:modelValue', false);
    }

    return {
      formRows,
      dialogTitle,
      maxQuantity,
      canSubmit,
      formatCurrency,
      setAmount,
      syncAmountFromInput,
      incrementQuantity,
      decrementQuantity,
      handleClose,
      handleBackdropClick,
      handleSubmit,
    };
  },
});
</script>

<style lang="scss" scoped>
.donation-dialog-backdrop {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.8);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.3s ease;
}

.donation-dialog {
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  max-width: 480px;
  width: 92%;
  max-height: 90vh;
  overflow-y: auto;
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.dialog-title {
  font-size: 1.35rem;
  font-weight: 700;
  margin: 0;
  flex: 1;
  padding-right: 1rem;
}

.dialog-subtitle {
  margin: 0 0 16px;
  color: #666;
  font-size: 0.9rem;
}

.close-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: #f5f5f5;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.item-forms {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.item-form {
  border: 1px solid #eee;
  border-radius: 12px;
  padding: 16px;
}

.item-header {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}

.item-thumb {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  object-fit: cover;
}

.item-title {
  font-weight: 600;
  font-size: 1rem;
}

.item-mode-label {
  font-size: 0.8rem;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.donation-label {
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.input-wrapper {
  display: flex;
  align-items: center;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 0 12px;
  margin-bottom: 12px;

  .input-icon {
    color: #888;
    margin-right: 4px;
  }

  .amount-input {
    border: none;
    outline: none;
    width: 100%;
    padding: 12px 0;
    font-size: 1rem;
  }
}

.amount-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.amount-btn {
  border: 1px solid #ddd;
  background: #fff;
  border-radius: 999px;
  padding: 8px 14px;
  cursor: pointer;
  font-weight: 600;

  &.active {
    border-color: #1fbeaf;
    background: rgba(31, 190, 175, 0.12);
    color: #0d8f82;
  }
}

.ticket-price-note {
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 8px;
}

.ticket-qty-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.ticket-qty-label {
  font-weight: 600;
}

.shop-qty-stepper {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.shop-qty-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid #ddd;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.shop-qty-value {
  min-width: 24px;
  text-align: center;
  font-weight: 600;
}

.line-total {
  margin-top: 10px;
  font-weight: 600;
}

.error-message {
  margin-top: 8px;
  color: #d93025;
  font-size: 0.85rem;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
}

.action-btn {
  border: none;
  border-radius: 10px;
  padding: 14px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
}

.primary-btn {
  background: #1fbeaf;
  color: #fff;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.secondary-btn {
  background: #f5f5f5;
  color: #333;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
